/**
 * Neumind FAQ Data Source
 * Centralised source of truth for website FAQs.
 * Strategies derived from K1 (Product), K3 (Personas), and Med-Legal Reports.
 * 
 * MAPPING:
 * - 'solution-client':       Solutions for Individuals & Families
 * - 'solution-professional': Solutions for Professionals & Teams
 * - 'pricing-client':        Pricing (Individuals & Families Tab)
 * - 'pricing-professional':  Pricing (Professionals Tab)
 * - 'pricing-casemanager':   Pricing (Case Managers & Solicitors Tab)
 */

export type FAQTag =
    | 'solution-client'
    | 'solution-professional'
    | 'pricing-client'
    | 'pricing-professional'
    | 'pricing-casemanager';

export interface FAQItem {
    question: string;
    answer: string;
    tags: FAQTag[];
    order?: number;
}

export const AllFAQs: FAQItem[] = [
    // =========================================================================
    // AUDIENCE: CASE MANAGERS & SOLICITORS
    // Strategy: PD 19B Compliance, "Hard Cost" Justification, Risk Mitigation.
    // =========================================================================
    {
        question: "How is Neumind classified for funding purposes? Is it an overhead?",
        answer: "Neumind is typically classified as a Recoverable Disbursement (Hard Cost), not a firm overhead. It is a 'Clinical Cognitive Prosthetic'—a specialist rehabilitation aid specific to the client's needs. Under Practice Direction 19B (Paragraph 22), it qualifies as a 'Specialist Service' allowing it to be funded directly from the client’s assets or settlement.",
        tags: ['pricing-casemanager'],
        order: 10
    },
    {
        question: "Can we pay via Invoice or Purchase Order?",
        answer: "Yes. The 'Recovery Arc' and 'Funded Case' licences are specifically designed for invoice-based procurement. We accept Purchase Orders (POs), provide standard 30-day payment terms, and manage the annual renewal process directly with your finance team to minimise administration.",
        tags: ['pricing-casemanager'],
        order: 20
    },
    {
        question: "What happens if funding is delayed?",
        answer: "We understand that obtaining Deputy or Insurer approval can take weeks. With our 'Bridge Access' guarantee, we grant your client full, immediate access to the platform upon your request. We then handle the administrative lag in the background, ensuring rehabilitation is never paused due to paperwork.",
        tags: ['pricing-casemanager', 'pricing-professional'],
        order: 30
    },
    {
        question: "Why does the Case Management licence cost more than the consumer app?",
        answer: "The professional licence covers the 'Service Wrapper' required for complex litigation cases. This includes court-ready outcome reporting, priority clinical onboarding, invoice-based billing, unlimited MDT user seats, and long-term data retention for legal compliance.",
        tags: ['pricing-casemanager'],
        order: 40
    },
    {
        question: "How does this support my court reporting?",
        answer: "Neumind provides objective, longitudinal data on your client's engagement, mood stability, and memory function. You can export these reports to evidence capacity and the efficacy of the interventions you are funding, supporting funding continuation requests.",
        tags: ['pricing-casemanager', 'solution-professional'],
        order: 50
    },

    // =========================================================================
    // AUDIENCE: PROFESSIONALS (CLINICIANS & TEAMS)
    // Strategy: Workflow efficiency, Liability protection, & Transferability.
    // =========================================================================
    {
        question: "Can I transfer a 'Practice Seat' to a new client?",
        answer: "Yes. Practice licences act as 'concurrent user' seats. When you discharge a patient, you can reassign that licence seat to a new intake instantly, making it a flexible asset for your clinic's fluctuating caseload.",
        tags: ['pricing-professional'],
        order: 10
    },
    {
        question: "What happens to patient data if a subscription ends?",
        answer: "Data integrity is paramount. If a client's subscription lapses or they are discharged, the clinical data remains accessible to you in 'Read-Only' mode for your records, ensuring continuity of care documentation is preserved.",
        tags: ['pricing-professional', 'solution-professional'],
        order: 20
    },
    {
        question: "Do I have to handle the payments for my clients?",
        answer: "Not necessarily. While you can purchase 'Practice Funded' seats for volume discounts, you can also use our 'Client Funded' workflow. You simply draft the plan, and the client (or their fundholder) completes the subscription payment securely via the app.",
        tags: ['pricing-professional'],
        order: 30
    },
    {
        question: "Is Neumind a medical device?",
        answer: "Neumind is a Digital Clinical Information System designed to support rehabilitation and executive function. It is not a medical device and does not provide automated diagnosis. It operates under strict clinical safety standards and GDPR compliance.",
        tags: ['pricing-professional', 'solution-professional'],
        order: 40
    },
    {
        question: "Will this increase my administrative workload?",
        answer: "Neumind is designed to reduce admin. By providing a 'digital support worker' that prompts clients and tracks adherence automatically, users report saving ~30 minutes per week per client on check-in calls and session planning.",
        tags: ['solution-professional'],
        order: 50
    },

    // =========================================================================
    // AUDIENCE: INDIVIDUALS & FAMILIES
    // Strategy: Simplicity, Family inclusion, & Risk reversal.
    // =========================================================================
    {
        question: "Do I need to pay for my family members?",
        answer: "No. Your subscription includes your entire 'Care Circle'. You can invite unlimited family members, friends, or carers to join your team, track your progress, and support you for free.",
        tags: ['pricing-client', 'solution-client'],
        order: 10
    },
    {
        question: "Can I cancel if it doesn't work for me?",
        answer: "Absolutely. We offer a 14-day free trial on the Active plan so you can test it risk-free. You can cancel your monthly subscription at any time directly from the app settings—no questions asked.",
        tags: ['pricing-client'],
        order: 20
    },
    {
        question: "What is the difference between Neumind Active and Neumind Life?",
        answer: "'Neumind Active' is designed for intensive recovery, allowing you to connect directly with a clinician for remote monitoring. 'Neumind Life' is a lighter plan for long-term maintenance and daily independence tools without the clinical dashboard connection.",
        tags: ['pricing-client'],
        order: 30
    },
    {
        question: "Will this work for my specific injury?",
        answer: "Neumind is designed to support the cognitive challenges common to many neurological conditions, such as memory loss, fatigue, and attention deficits. It is widely used by survivors of TBI, Stroke, and other acquired brain injuries.",
        tags: ['solution-client'],
        order: 40
    },
    {
        question: "Is my data private?",
        answer: "You are in control. You decide exactly who sees your data (like your therapist or family), and you can revoke their access at any time. We use bank-grade encryption and never sell personal data.",
        tags: ['solution-client'],
        order: 50
    }
];

export function getFAQs(tag: string): FAQItem[] {
    return AllFAQs.filter(faq => faq.tags.includes(tag as FAQTag)).sort((a, b) => (a.order || 0) - (b.order || 0));
}